import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { sampleContent } from '../../assets/sampleContent/sampleContent';
import { mythById, mythPath, punchline, shareUrl, stampClass } from '../../utils/myths';
import LinkShareModal from '../sharedComponents/LinkShareModal';
import CopyReplyMenu from '../sharedComponents/CopyReplyMenu';
import { useSearchTracking } from '../../utils/track';

// Door 1: the most common takes, one tap each.
const TAKES = [
  { label: 'Fires', id: 1 },
  { label: 'Coal', id: 20 },
  { label: 'Battery cost', id: 36 },
  { label: 'Cold', id: 9 },
  { label: 'Chargers', id: 14 },
  { label: 'Tax credit', id: 31 },
];

// Door 2: where the visitor is in buying, and the one next step for each.
const STAGES = [
  { n: 'Step 1', label: 'Just curious', title: 'Start with the idiotic statements', body: "34 idiotic statements, fact-checked, so you know what's real before anyone tries to sell you anything.", cta: 'Read the statements', action: 'myths' },
  { n: 'Step 2', label: 'Picking a car', title: 'Match your EV, then your state', body: 'The EVs you can buy in the US, filtered to how you drive, plus what your state and utility still pay.', cta: 'Find my EV', to: '/find-your-ev', also: { label: 'Incentives in my state', to: '/incentives' } },
  { n: 'Step 3', label: 'Have a quote', title: 'Run the Deal-Checker', body: 'Paste the out-the-door sheet. We flag every junk fee an EV buyer should refuse. Nothing stored.', cta: 'Check my quote', to: '/deal-checker' },
  { n: 'Step 4', label: 'Signing soon', title: 'Get a deal review first', body: "Send us the numbers before you sign. We'll tell you exactly what's wrong with them. Free.", cta: 'Get help buying', to: '/concierge' },
];

const track = (name, params) => { if (window.gtag) window.gtag('event', name, params || {}); };

export default function Doors({ onSeeMyths }) {
  const [door, setDoor] = useState(0);
  const [takeId, setTakeId] = useState(TAKES[0].id);
  const [query, setQuery] = useState('');
  const [stage, setStage] = useState(2);
  const [shareOpen, setShareOpen] = useState(false);

  // Typing beats the chips: show the first myth whose title matches.
  const answer = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length >= 3) {
      const hit = sampleContent.find((s) => s.title.toLowerCase().includes(q))
        || sampleContent.find((s) => (s.categories || []).some((c) => c.toLowerCase().includes(q)));
      if (hit) return hit;
    }
    return mythById(takeId);
  }, [query, takeId]);

  const noMatch = query.trim().length >= 3 && !sampleContent.some((s) =>
    s.title.toLowerCase().includes(query.trim().toLowerCase())
    || (s.categories || []).some((c) => c.toLowerCase().includes(query.trim().toLowerCase())));

  const step = STAGES[stage];

  // `search` once they stop typing; results is 1 when a statement matched, 0 when not.
  useSearchTracking(query, 'home_doors', query.trim().length >= 3 ? (noMatch ? 0 : 1) : undefined);

  return (
    <div className="fi-doors" data-track-context="home_doors">
      <div className="fi-dtabs" role="tablist" aria-label="Why you're here">
        {['Settle an argument', 'Get help buying'].map((label, i) => (
          <button
            key={label}
            type="button"
            role="tab"
            id={`fi-door-tab-${i}`}
            aria-selected={door === i}
            aria-controls={`fi-door-panel-${i}`}
            className={`fi-dtab${door === i ? ' is-on' : ''}`}
            onClick={() => { setDoor(i); track('door_open', { door: i + 1 }); }}
          >
            <small>Door {i + 1}</small>
            <b>{label}</b>
          </button>
        ))}
      </div>

      {door === 0 && answer && (
        <div className="fi-dbody" role="tabpanel" id="fi-door-panel-0" aria-labelledby="fi-door-tab-0">
          <label className="fi-q">
            <svg viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <span className="fi-sr">What did they say?</span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="What did they say? fires, coal, battery..."
            />
          </label>
          <div className="fi-chips" role="group" aria-label="Common idiotic statements">
            {TAKES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`fi-chip${!query && takeId === t.id ? ' is-on' : ''}`}
                aria-pressed={!query && takeId === t.id}
                onClick={() => { setQuery(''); setTakeId(t.id); track('door1_take', { take: t.label }); }}
              >
                {t.label}
              </button>
            ))}
          </div>
          {noMatch && <p style={{ margin: 0, fontSize: 14 }}>No exact match, here's a popular one. All 34 are in the fact-check desk below.</p>}
          <div className="fi-ans" aria-live="polite">
            <div className="fi-ans-top">
              <span className={stampClass(answer)}>{answer.titleBadge}</span>
              <b>{answer.title}</b>
            </div>
            <p>{punchline(answer)}</p>
            <div className="fi-ans-f">
              <button type="button" className="fi-btn fi-btn--sm fi-btn--ink" onClick={() => { setShareOpen(true); track('door1_share', { title: answer.title }); }}>
                Share with an idiot
              </button>
              <CopyReplyMenu title={answer.title} verdict={answer.titleBadge} answer={punchline(answer)} shareLink={shareUrl(answer)} />
              <RouterLink to={mythPath(answer)} style={{ fontWeight: 800, color: '#111' }}>Read the receipts</RouterLink>
            </div>
          </div>
          <LinkShareModal link={shareUrl(answer)} modalOpen={shareOpen} handleCloseModal={() => setShareOpen(false)} idiotTypes={answer.idiotTypes} />
        </div>
      )}

      {door === 1 && (
        <div className="fi-dbody" role="tabpanel" id="fi-door-panel-1" aria-labelledby="fi-door-tab-1">
          <div className="fi-stages" role="group" aria-label="Where you are in buying">
            {STAGES.map((s, i) => (
              <button
                key={s.label}
                type="button"
                className={`fi-stg${stage === i ? ' is-on' : ''}`}
                aria-pressed={stage === i}
                onClick={() => { setStage(i); track('door2_stage', { stage: s.label }); }}
              >
                <small>{s.n}</small>{s.label}
              </button>
            ))}
          </div>
          <div className="fi-next">
            <span className="fi-nl">Your next step</span>
            <b>{step.title}</b>
            <span className="fi-nbody">{step.body}</span>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
              {step.action === 'myths' ? (
                <button type="button" className="fi-btn fi-btn--sm fi-btn--ink" onClick={() => { track('door2_cta', { stage: step.label }); onSeeMyths(); }}>
                  {step.cta}
                </button>
              ) : (
                <RouterLink className="fi-btn fi-btn--sm fi-btn--ink" to={step.to} onClick={() => track('door2_cta', { stage: step.label })}>
                  {step.cta}
                </RouterLink>
              )}
              {step.also && <RouterLink to={step.also.to} style={{ fontWeight: 800, color: '#111' }}>{step.also.label}</RouterLink>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
